import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {GatewayPublicUserService, GatewayRoleResourceService, RoleDTO, RoleRuleDTO} from '@tj/tj-dmp-lib-api';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {AccountService} from '../../../auth/account.service';
import {TranslateService} from '@ngx-translate/core';
import {forkJoin} from 'rxjs';
import { DEFAULT_ROLES, LOCKED_ROLE, RoleRulesService, RuleTemplate } from '../../../services/role-rules.service';

@Component({
    templateUrl: './role.component.html',
    styleUrls: ['./role.component.scss'],
    providers: [MessageService],
    standalone: false
})
export class RoleComponent implements OnInit {

    breadcrumb: MenuItem[] = [
        {label: this.translate.instant('menu.admin.label')},
        {label: this.translate.instant('menu.admin.roles')}
    ];
    description = 'Voici la gestion des rôle' as const;


    roles: RoleDTO[];

    actual: RoleDTO = null;

    showDetails = false;

    state: { message: string };

    dataRules: RuleTemplate[] = [];

    // Rules known by the API (GET /api/rule/), and the action menu of each role.
    private apiRules: string[] = [];
    menus: Record<string, MenuItem[]> = {};

    items = [
        {
            label: "Visualisation",
            items: [
                {
                    label: "Plus d'info",
                    icon: "fas fa-user",
                    permission: "ROLES.read",
                    command: () => {
                        this.showRoleDetails();
                    },
                },
            ],
        },
        {
            label: 'Gestion',
            items: [
                {
                    id: 'edit',
                    label: 'Editer',
                    icon: 'fas fa-pen',
                    permission: 'ROLES.edit',
                    command: () => {
                        this.router.navigate([`/h/admin/role/edit/${this.actual.name}`]);
                    }
                },
                {
                    id: 'delete',
                    label: 'Supprimer',
                    icon: 'fas fa-trash',
                    permission: 'ROLES.delete',
                    command: () => {
                        this.confirmationService.confirm({
                            message: `Êtes vous sur de supprimer le rôle ${this.actual.name}`,
                            accept: () => {
                                // this.userService.deleteUserUsingDELETE(this.actual.login)
                                this.gatewayRoleResourceService._delete(this.actual.name).subscribe(() => {
                                    this.loadAll();
                                    this.messageService.add({
                                        severity: 'success',
                                        summary: 'Succès',
                                        detail: 'Le rôle a bien été supprimé'
                                    });
                                });
                            }
                        });
                    }
                }]
        },
    ];

    constructor(
        private confirmationService: ConfirmationService,
        private accountService: AccountService,
        private messageService: MessageService,
        private router: Router,
        private gatewayPublicUserService: GatewayPublicUserService,
        private readonly gatewayRoleResourceService: GatewayRoleResourceService,
        private readonly translate: TranslateService,
        private roleRulesService: RoleRulesService) {
        const navigation = this.router.getCurrentNavigation();
        const state = navigation?.extras?.state as { message: string } | undefined;
        this.state = state;
    }

    ngOnInit(): void {
        this.loadAll();

    }


    loadAll() {
        console.log('load');
        forkJoin([this.gatewayRoleResourceService.getRules(), this.gatewayRoleResourceService.getRoles()]).subscribe(([rules, res]) => {
                this.apiRules = rules;
                this.roles = res;
                this.menus = {};
                res.forEach(role => this.menus[role.name] = this.menuFor(role));
            },
            (e) => {
                this.messageService.add({severity: 'error', summary: 'Erreur', detail: `Un problème est survenu: ${e.toString()}`});
            });
    }

    // Default roles cannot be deleted, and ROLE_ADMIN cannot be changed: their actions are left out, and so is a
    // group left without action.
    private menuFor(role: RoleDTO): MenuItem[] {
        const hidden = new Set<string>();
        if (DEFAULT_ROLES.includes(role.name)) {
            hidden.add('delete');
        }
        if (role.name === LOCKED_ROLE) {
            hidden.add('edit');
        }
        return (this.items as MenuItem[])
            .map(group => ({...group, items: group.items.filter(item => !hidden.has(item.id))}))
            .filter(group => group.items.length > 0);
    }

    showRoleDetails() {
        if (this.actual && this.actual.rules) {
            this.transformRulesForDisplay();
            this.showDetails = true;
        }
    }

    navigateToDetail(role: RoleDTO){
        this.actual = role;
        this.showRoleDetails();
    }

    private transformRulesForDisplay() {
        const existingPermissions: { [key: string]: string } = {};

        this.actual.rules.forEach((roleRule: RoleRuleDTO) => {
            const {level, feature} = RoleRulesService.splitRule(roleRule.rule);
            existingPermissions[feature] = level;
        });

        this.dataRules = this.roleRulesService.getRules(this.apiRules);
        this.dataRules.forEach(displayRule => {
            const permission = existingPermissions[displayRule.rule];

            if (permission === 'READ') {
                displayRule.read = true;
            } else if (permission === 'EDIT') {
                displayRule.edit = true;
            } else if (permission === 'WRITE') {
                displayRule.write = true;
            }
        });

        this.updateCalculatedRules();
    }

    private updateCalculatedRules() {
        const importsRule = this.dataRules.find(r => r.rule === 'IMPORTS');
        const exportsRule = this.dataRules.find(r => r.rule === 'EXPORTS');
        const dataviewsRule = this.dataRules.find(r => r.rule === 'DATAVIEWS');

        // DICO hérite d'IMPORTS
        const dicoRule = this.dataRules.find(r => r.rule === 'DICO');
        if (dicoRule && importsRule) {
            dicoRule.read = importsRule.read;
            dicoRule.edit = importsRule.edit;
            dicoRule.write = importsRule.write;
        }

        // TASKS_IMPORT hérite d'IMPORTS
        const tasksImportRule = this.dataRules.find(r => r.rule === 'TASKS_IMPORT');
        if (tasksImportRule && importsRule) {
            tasksImportRule.read = importsRule.read;
            tasksImportRule.edit = importsRule.edit;
            tasksImportRule.write = importsRule.write;
        }

        // TASKS_EXPORT hérite d'EXPORTS
        const tasksExportRule = this.dataRules.find(r => r.rule === 'TASKS_EXPORT');
        if (tasksExportRule && exportsRule) {
            tasksExportRule.read = exportsRule.read;
            tasksExportRule.edit = exportsRule.edit;
            tasksExportRule.write = exportsRule.write;
        }

        // TAGEVENTS hérite de DATAVIEWS
        const tagEventsRule = this.dataRules.find(r => r.rule === 'TAGEVENTS');
        if (tagEventsRule && dataviewsRule) {
            tagEventsRule.read = dataviewsRule.read;
            tagEventsRule.edit = dataviewsRule.edit;
            tagEventsRule.write = dataviewsRule.write;
        }
    }
}
